/**
 * EventProseSection — a heading and a block of text an organiser typed.
 *
 * Both prerequisites and preparation steps are optional at creation, so each
 * renders ONLY when there is something to read. An empty "Prerequisites"
 * heading tells a visitor there are requirements and then declines to say what
 * they are, which is worse than not raising the subject.
 *
 * Prerequisites and preparation steps are rendered as text, never as markup:
 * they come from an organiser's plain form field, and a public page is the wrong
 * place to find out that someone pasted HTML into one. Line breaks are honoured
 * by the stylesheet (`white-space: pre-line`) so a typed list still reads as a
 * list.
 *
 * Event and activity DESCRIPTIONS are different — those two fields are edited
 * with a formatting toolbar and store HTML. They opt in with `rich`, which is
 * off by default precisely so the fields above, and the event title, keep the
 * behaviour they have always had.
 */

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { sanitizeRichText, richTextToPlain, looksLikeHtml } from '../../../utils/sanitizeRichText';

/**
 * Whitespace-only is empty. A field of spaces is not content.
 *
 * Markup counts as empty too: a toolbar that has been clicked in and then
 * cleared leaves `<p></p>`, which is a heading and a blank space where a
 * visitor expected to read something.
 */
export function hasProse(value) {
  if (typeof value !== 'string') return Boolean(value);
  if (looksLikeHtml(value)) return richTextToPlain(value).trim() !== '';
  return value.trim() !== '';
}

/**
 * A paragraph of organizer-written text that stays a sensible height.
 *
 * The text columns are unbounded, so a description can be thousands of lines
 * long and push everything below it off the page. This clamps to `lines` and
 * offers a toggle — but only when the text actually overflows, so ordinary
 * short copy is untouched and gains no stray button.
 */
export function ProseParagraph({ text, className = 'evb-p', lines = 10, as: Tag = 'p', rich = false }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Rich only when the caller asked AND the value actually carries markup. A
  // description saved before the toolbar existed is plain text, and it must go
  // on rendering exactly as it did — detection here, never a migration there.
  const isRich = rich && looksLikeHtml(text);
  const html = useMemo(() => (isRich ? sanitizeRichText(text) : null), [isRich, text]);

  useLayoutEffect(() => {
    const el = ref.current;
    // Measure only while collapsed: expanded, the clamp is off and
    // scrollHeight equals clientHeight, which would retract the toggle
    // and trap the reader in the open state with no way back.
    if (!el || expanded) return undefined;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 2);
    measure();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, expanded]);

  // A <p> may not contain a <p>, and rich text is paragraphs. The element has
  // to be a div once there is markup inside it — the class list is what carries
  // the styling, so nothing about how it reads changes.
  const Element = isRich ? 'div' : Tag;
  const classNames = `${className}${isRich ? ' evb-rich' : ''}${expanded ? '' : ' evb-clamp'}`;
  const style = expanded ? undefined : { '--evb-lines': lines };

  return (
    <>
      {isRich ? (
        <Element
          ref={ref}
          className={classNames}
          style={style}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Element ref={ref} className={classNames} style={style}>
          {text}
        </Element>
      )}
      {overflowing && (
        <button
          type="button"
          className="evb-more"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}

export default function EventProseSection({ heading, text }) {
  if (!hasProse(text)) return null;

  return (
    <section className="evb-sec">
      <h2 className="evb-h">{heading}</h2>
      {/* <p className="evb-p evb-prose">{text}</p> */}
      <ProseParagraph text={text} className="evb-p evb-prose" />
    </section>
  );
}
